import React, {
  useState, useRef, useCallback, useContext, useEffect, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  faPrint,
  faSave,
  faPlus,
  faFileExcel,
  faFilePdf,
  faFileCode, faTableList, faTableColumns, faTableCells, faSort, faFilter, faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Alert, TabContext, TabList, TabPanel,
} from '@mui/lab';
import {
  Box,
  Button,
  Dialog, DialogActions,
  DialogContent,
  Fade,
  IconButton,
  Paper,
  Tab,
  Toolbar,
  Typography,
} from '@mui/material';
import {
  CloseButton, GenerateButton, SettingsButton,
} from '../../components/Styled/Buttons';
import useReportEditor from './hooks';
import ReportSettingsEditor from './ReportSettingsEditor';
import Results from './results';
import ParamsField from './reportParams';
import SaveReport from './saveReport';
import ContextMenu from './results/contextMenu';
import DetailsShower from './detailsShower';
import Loader from '../../components/MaterialUI/loader';
import WinManagerContext from '../../providers/winManagerProvider';

const SHOW_MODES = {
  results: 'RESULTS',
  settings: 'SETTINGS',
  saveNew: 'SAVENEW',
  save: 'SAVE',
};

const settingsTabPanelsx = {
  p: 0,
  flex: '1 1 auto',
  overflow: 'hidden',
};

function StdReportEditor({
  reportId, reportUrl,
  hideGroups, hideColumnGroups, hideFilters, hideSelections, hideOrders, defaultSettingsPage,
}) {
  const {
    reportData,
    schema,
    availableGroups,
    groups,
    groupsHandlers,
    availableColumns,
    columns,
    columnsHandlers,
    availableSelections,
    selections,
    selectionsHandlers,
    availableOrders,
    orders,
    ordersHandlers,
    availableFilters,
    filters,
    filtersHandlers,
    params,
    availableParams,
    paramsHandlers,
    onGenerateReport,
    results,
    loading,
    onGenerateReportXLSX,
    onGenerateReportHTML,
    onGenerateReportPDF,
    onSaveReport,
    onAbortReport,
    error,
    onGetSettings,
    permissions,
  } = useReportEditor(reportId);
  const { setWindowTitle } = useContext(WinManagerContext);
  const [showMode, setShowMode] = useState(SHOW_MODES.results);
  const [updated, setUpdated] = useState(false);
  // const [isLandScape, setLandScape] = useState(false);
  const [typeOfReport, setTypeOfReport] = useState('report');

  const resultRef = useRef(null);
  const scrollerRef = useRef(null);

  const print = useCallback(
    () => {
      if (results && resultRef.current) {
        const el = resultRef.current.cloneNode(true);
        const printDiv = document.getElementById('print-area');
        printDiv.appendChild(el);
        window.print();
        printDiv.removeChild(el);
      }
      return true;
    },
    [results],
  );

  const [contextMenu, setContextMenu] = useState({
    opened: false,
    x: 0,
    y: 0,
    detail: null,
    allowedDetails: [],
  });
  const onContextMenu = useCallback(
    (e, contextGroups, contextData) => {
      const knowFields = new Set([
        ...Object.keys(contextGroups.row),
        ...Object.keys(contextGroups.column),
      ]);
      const currentField = [...knowFields]
        .filter((kf) => kf in schema.src.meta_fields
          && schema.src.meta_fields[kf].key in contextData)
        .reduce((R, r) => ({
          ...schema.src.meta_fields[r],
          name: r,
        }), null);
      if (!currentField) return false;
      const usedGroups = new Set([
        ...groups.reduce((R, sg) => [...R, ...(sg.items.map((g) => g.name))], []),
        ...columns.reduce((R, sc) => [...R, ...(sc.items.map((c) => c.name))], []),
      ]);
      const allowedGroups = availableGroups.filter((a) => !usedGroups.has(a.name));

      setContextMenu({
        opened: true,
        x: e.clientX,
        y: e.clientY,
        detail: {
          currentItem: {
            backend: currentField.resource,
            key: currentField.key,
            name: currentField.name,
            id: contextData[currentField.key],
            repr: contextData.repr,
          },
        },
        allowedDetails: allowedGroups,
      });
      e.preventDefault();
      return false;
    },
    [schema, groups, columns, availableGroups],
  );

  const [detail, setDetail] = useState({
    settings: {},
    opened: false,
  });

  const onDetail = useCallback(
    (e, group, currentItem) => {
      const oldSettings = onGetSettings();
      const newFIlters = [
        ...oldSettings.filters,
        [currentItem.name, '=', {
          id: currentItem.id,
          repr: currentItem.repr,
        }, true],
      ];
      const newGroups = [...oldSettings.groups, [{
        key: group.name,
        hierarchy: false,
      }]];
      setDetail({
        settings: {
          ...oldSettings,
          groups: newGroups,
          filters: newFIlters,
        },
        opened: true,
      });
      setContextMenu(({ ...r }) => ({
        ...r,
        opened: false,
      }));
    },
    [onGetSettings],
  );

  const handleGenerateReport = useCallback(() => {
    if (typeOfReport === 'Excel') {
      return onGenerateReportXLSX.onAbortReport();
    }
    if (typeOfReport === 'HTML') {
      return onGenerateReportHTML.onAbortReport();
    }
    if (typeOfReport === 'PDF') {
      return onGenerateReportPDF.onAbortReport();
    }
    return onAbortReport();
  }, [
    onAbortReport,
    onGenerateReportHTML,
    onGenerateReportPDF,
    onGenerateReportXLSX,
    typeOfReport,
  ]);

  const [currentTab, setCurrentTab] = useState(defaultSettingsPage);

  const onCloseDetailDialog = useCallback(
    () => setDetail(
      ({ ...r }) => ({
        ...r,
        opened: false,
      }),
    ),
    [],
  );
  useEffect(
    () => {
      if (reportData.name) setWindowTitle(reportUrl, reportData.name);
    },
    [reportData.name, reportUrl, setWindowTitle],
  );
  const caption = useMemo(
    () => (results ? (results.header || reportData.name) : ''),
    [reportData.name, results],
  );

  useEffect(
    () => {
      if (showMode && scrollerRef.current) {
        scrollerRef.current.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
      }
    },
    [showMode],
  );
  return (
    <DndProvider backend={HTML5Backend}>
      <Box sx={{
        display: 'flex', flexDirection: 'column', height: 1, overflow: 'hidden',
      }}
      >
        <Box>
          <Typography variant="h6" color="success.main">{reportData.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {reportData.description}
          </Typography>
          {error && (
          <Alert severity="error">
            {error}
          </Alert>
          )}
          <Box display="flex" gap={2}>
            {availableParams && availableParams.map((param) => (
              <Box flex="1 1 auto" key={param.name}>
                <ParamsField
                  param={param}
                  currentParam={params[param.name]}
                  changeParam={paramsHandlers.changeParams}
                  readOnly={!permissions.params}
                />
              </Box>
            ))}
          </Box>
          <Toolbar variant="dense">
            <GenerateButton
              onClick={() => {
                setTypeOfReport('report');
                onGenerateReport();
                setShowMode(SHOW_MODES.results);
                setUpdated(true);
              }}
              content="Сформувати звіт"
            />
            <SettingsButton
              onClick={() => setShowMode(
                (sm) => (sm === SHOW_MODES.settings ? SHOW_MODES.results : SHOW_MODES.settings),
              )}
              disabled={!permissions.filters && !permissions.fields && !permissions.orders && !permissions.groups && !permissions.columns}
            />
            <IconButton
              onClick={() => print()}
              title="Друк"
              color="primary"
            >
              <FontAwesomeIcon icon={faPrint} />
            </IconButton>
            <IconButton
              onClick={() => {
                setTypeOfReport('Excel');
                onGenerateReportXLSX.onGenerateReport();
              }}
            // disabled={!results || showMode !== SHOW_MODES.results}
              title="Зберегти в Excel"
              color="success"
            >
              <FontAwesomeIcon icon={faFileExcel} />
            </IconButton>
            <IconButton
              onClick={() => {
                setTypeOfReport('HTML');
                onGenerateReportHTML.onGenerateReport();
              }}
            // disabled={!results || showMode !== SHOW_MODES.results}
              title="Зберегти в HTML"
              color="warning"
            >
              <FontAwesomeIcon icon={faFileCode} />
            </IconButton>
            <IconButton
              onClick={() => {
                setTypeOfReport('PDF');
                onGenerateReportPDF.onGenerateReport();
              }}
            // disabled={!results || showMode !== SHOW_MODES.results}
              title="Зберегти в PDF"
              color="error"
            >
              <FontAwesomeIcon icon={faFilePdf} />
            </IconButton>

            {/* <Button */}
            {/*  onClick={() => { */}
            {/*    setLandScape(!isLandScape); */}
            {/*  }} */}
            {/*  disabled={!results || showMode !== SHOW_MODES.results} */}
            {/*  title={isLandScape ? 'Альбомна' : 'Портретна'} */}
            {/* > */}
            {/*  <SFontAwesomeIcon icon={isLandScape ? faImage : faFileImage} /> */}
            {/* </Button> */}
            <IconButton
              onClick={() => {
                setShowMode(SHOW_MODES.save);
              }}
              title="Зберегти звіт"
              color="primary"
            >
              <FontAwesomeIcon icon={faSave} />
            </IconButton>
            <IconButton
              onClick={() => {
                setShowMode(SHOW_MODES.saveNew);
              }}
              title="Створити новий звіт"
              color="primary"
            >
              <FontAwesomeIcon icon={faPlus} />
            </IconButton>

          </Toolbar>
        </Box>
        <Box
          ref={scrollerRef}
          sx={{
            overflow: 'auto',
            boxSizing: 'border-box',
          }}
        >
          {loading && (
          <Loader text="Завантаження">
            <Button
              variant="text"
              size="small"
              color="warning"
              disabled={!updated}
              startIcon={<FontAwesomeIcon icon={faTimes} size="xs" />}
              fullWidth
              sx={{ mt: 2 }}
              onClick={handleGenerateReport}
            >
              Скасувати запит
            </Button>
          </Loader>
          )}
          <Fade in={showMode === SHOW_MODES.save} unmountOnExit>
            <Paper>
              <Typography color="success.main" variant="h6" sx={{ p: 2 }}>Зберегти звіт</Typography>
              <SaveReport
                onHide={() => setShowMode(SHOW_MODES.results)}
                reportData={reportData}
                onSave={onSaveReport}
              />
            </Paper>
          </Fade>
          <Fade in={showMode === SHOW_MODES.saveNew} unmountOnExit>
            <Paper>
              <Typography color="success.main" variant="h6" sx={{ p: 2 }}>Зберегти звіт</Typography>
              <SaveReport
                onHide={() => setShowMode(SHOW_MODES.results)}
                isNew
                reportData={reportData}
                onSave={onSaveReport}
              />
            </Paper>
          </Fade>
          <Fade in={showMode === SHOW_MODES.settings} unmountOnExit>
            <Paper
              sx={{
                height: 1,
                boxSizing: 'border-box',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <TabContext value={currentTab}>
                <Box sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
                >
                  <TabList onChange={(e, v) => setCurrentTab(v)} variant="scrollable" scrollButtons="auto">
                    {!hideGroups && permissions.groups && (
                    <Tab
                      icon={(<FontAwesomeIcon icon={faTableList} size="lg" />)}
                      label="Групування"
                      value="groups"
                      iconPosition="start"
                    />
                    )}
                    {!hideColumnGroups && permissions.columns && (
                    <Tab
                      icon={(<FontAwesomeIcon icon={faTableColumns} size="lg" />)}
                      label="Групування колонок"
                      value="columns"
                      iconPosition="start"
                    />
                    )}
                    {!hideSelections && permissions.fields && (
                    <Tab
                      icon={(<FontAwesomeIcon icon={faTableCells} size="lg" />)}
                      label="Обрані поля"
                      value="selection"
                      iconPosition="start"
                    />
                    )}
                    {!hideOrders && permissions.orders && (
                    <Tab
                      icon={(<FontAwesomeIcon icon={faSort} size="lg" />)}
                      label="Сортування"
                      value="orders"
                      iconPosition="start"
                    />
                    )}
                    {!hideFilters && permissions.filters && (
                    <Tab
                      icon={(<FontAwesomeIcon icon={faFilter} size="lg" />)}
                      label="Фільтрування"
                      value="filters"
                      iconPosition="start"
                    />
                    )}
                  </TabList>
                </Box>
                {!hideGroups && permissions.groups && (
                <TabPanel value="groups" sx={settingsTabPanelsx}>
                  <ReportSettingsEditor.GroupEditor
                    availableGroups={availableGroups}
                    groups={groups}
                    groupsHandlers={groupsHandlers}
                  />
                </TabPanel>
                )}
                {!hideColumnGroups && permissions.columns && (
                <TabPanel value="columns" sx={settingsTabPanelsx}>
                  <ReportSettingsEditor.ColumnEditor
                    availableColumns={availableColumns}
                    columns={columns}
                    columnsHandlers={columnsHandlers}
                  />
                </TabPanel>
                )}
                {!hideSelections && permissions.fields && (
                <TabPanel value="selection" sx={settingsTabPanelsx}>
                  <ReportSettingsEditor.SelectionEditor
                    availableSelections={availableSelections}
                    selections={selections}
                    selectionHandlers={selectionsHandlers}
                  />
                </TabPanel>
                )}
                {!hideOrders && permissions.orders && (
                <TabPanel value="orders" sx={settingsTabPanelsx}>
                  <ReportSettingsEditor.OrdersEditor
                    availableOrders={availableOrders}
                    orders={orders}
                    ordersHandlers={ordersHandlers}
                  />
                </TabPanel>
                )}
                {!hideFilters && permissions.filters && (
                <TabPanel value="filters" sx={settingsTabPanelsx}>
                  <ReportSettingsEditor.FiltersEditor
                    availableFilters={availableFilters}
                    filters={filters}
                    filtersHandlers={filtersHandlers}
                  />
                </TabPanel>
                )}
              </TabContext>
            </Paper>
          </Fade>
          {results && (
          <Fade in={showMode === SHOW_MODES.results} unmountOnExit>
            <Results
              result={results}
              ref={resultRef}
              caption={caption}
              onContextMenu={onContextMenu}
            />
          </Fade>
          )}
        </Box>
      </Box>
      <ContextMenu
        top={contextMenu.y}
        left={contextMenu.x}
        opened={contextMenu.opened}
        allowedColumns={[]}
        onCloseMenu={() => setContextMenu(({ ...r }) => ({
          ...r,
          opened: false,
        }))}
        detail={contextMenu.detail}
        allowedDetails={contextMenu.allowedDetails}
        onDetailProcessing={onDetail}
      />

      <Dialog
        open={detail.opened}
        onClose={onCloseDetailDialog}
        maxWidth="xl"
        fullWidth
        scroll="paper"
      >
        <DialogContent>
          <DetailsShower reportId={reportId} schema={schema} settings={detail.settings} />
        </DialogContent>
        <DialogActions>
          <CloseButton
            onClick={onCloseDetailDialog}
          />
        </DialogActions>
      </Dialog>
    </DndProvider>
  );
}

StdReportEditor.propTypes = {
  reportId: PropTypes.string.isRequired,
  reportUrl: PropTypes.string.isRequired,
  hideGroups: PropTypes.bool,
  hideColumnGroups: PropTypes.bool,
  hideFilters: PropTypes.bool,
  hideSelections: PropTypes.bool,
  hideOrders: PropTypes.bool,
  defaultSettingsPage: PropTypes.oneOf(['groups', 'columns', 'selection', 'orders', 'filters']),
};

StdReportEditor.defaultProps = {
  hideGroups: false,
  hideColumnGroups: false,
  hideFilters: false,
  hideSelections: false,
  hideOrders: false,
  defaultSettingsPage: 'selection',
};

export default StdReportEditor;
